<template>
  <div>
    <page-heading page-title="Real-Time Interface"
                  v-bind:left-text="'Welcome ' + session.userId"
                  v-bind:current-timestamp="utcNow" v-bind:start-timestamp="session.activeSessionStart"
                  v-bind:end-timestamp="session.activeSessionEnd"
                  v-bind:links="[
                    {target: '#status-info-modal', text: 'Status info'},
                    {target: '#help-modal', text: 'Help'},
                    {target: '/', text: 'Exit session'}
                  ]">
    </page-heading>
    <div class='container'>
      <div class="row">
        <div class="col">
          <template v-if="!isBridgeConnected">
            <page-alert>
              <div slot="message">Error connecting to the Real-time Interface</div>
            </page-alert>
          </template>
          <template v-if="!isAgentConnected">
            <page-alert>
              <div slot="message">Cannot connect to the agent at site {{ telescope.name }}</div>
            </page-alert>
          </template>
        </div>
      </div>
      <div class='row'>
        <div class='col'>
          <SessionStatus :agentStatus="agentStatus"
                         :utcNow="utcNow"
                         :telescope="telescope"
                         :sessionActive="sessionActive"
                         :session="session"
                         :exposure-times="submittedExposureTimes">
          </SessionStatus>
          <ObservationPanel class="pt-3"
                            :agentStatus="agentStatus"
                            :utcNow="utcNow"
                            :sessionActive="sessionActive"
                            :session="session"
                            :rtiBridgeUrl="rtiBridgeUrl"
                            :telescope="telescope"
                            v-on:sendSubmittedExposureTimes="setSubmittedExposureTimes">
          </ObservationPanel>
        </div>
      </div>
      <help-modal>
        <div slot="help-text">
          <p>
            Choose a target input mode, either <strong>List</strong> or <strong>Manual</strong>. When
            <strong>Manual</strong>
            is selected, the target name, right ascension, and declinations must all be
            entered into their respective fields. Note that the 0.4-meter telescope will not point within 0.5 degrees
            of the pole (+/-90 degrees declination). When <strong>List</strong> mode is selected, the <strong>Target
            Name</strong> field turns into a drop-down menu listing targets that are currently visible. If you hover
            over a target in the menu, a description of that target will appear. Upon selecting one of those targets,
            the right ascension and declination fields are auto-populated.
          </p>
          <p>
            After selecting a target, choose a filter from the drop-down filter list. If the target-selection mode
            is <strong>List</strong>, a recommended exposure time will populate the <strong>Exposure Time</strong>
            field, which can be changed manually if necessary. Note that an observation will take longer than the
            exposure time- the total time it takes for an observation to complete includes processing and readout
            time.
          </p>
          <p>
            After filling in the exposure configuration, the <strong>GO!</strong>
            button can be clicked. Doing this
            will send the observation request to the telescope. After clicking the button, <strong>Telescope
            State</strong>, <strong>Camera State</strong>, and <strong>Progress</strong>
            should change to reflect
            current states. This button will not function until the session has started or after the
            session has ended. If there is insufficient time remaining in the session to complete the observation,
            the observation will not be allowed to start.
          </p>
          <p>
            The <strong>Session Images</strong>
            table shows a thumbnail of the latest observation as well as a list of observations from the current
            session. These images are available to download by clicking the link in the <strong>Download</strong>
            column. It will take a few minutes for the images to appear in the table, and for them to be available for
            download. Note that after a session has ended, observations will continue to be available through the
            <a :href="archiveLink" target="_blank">LCO Science Archive</a>.
          </p>
        </div>
      </help-modal>
      <status-info-modal>
        <div slot="status-info-text">
          <p class="h4">Session status</p>
          <p>
          <ul class="list-unstyled">
            <li><strong>Not yet started</strong>
              The session has not yet started.
            </li>
            <li><strong>Ended</strong>
              The session has ended.
            </li>
            <li><strong>In progress</strong>
              The session is currently under way.
            </li>
          </ul>
          <p class="h4">Observatory status</p>
          <p>
          <ul class="list-unstyled">
            <li>
              <strong>Sun-up</strong>
              Sun-up The telescope cannot be used because there is a risk of sunlight hitting the optics.
            </li>
            <li>
              <strong>Closed for bad weather</strong>
              The telescope cannot be used because weather conditions preclude observing. These might be conditions
              which could damage the telescope (e.g. rain or high winds) or conditions that could inhibit observations
              such as thick cloud layers.
            </li>
            <li>
              <strong>Closed for maintenance</strong>
              The telescope has been disabled or switched to manual for essential engineering activity.
            </li>
            <li>
              <strong>Closed for technical failure</strong>
              A technical failure is preventing observing. The failure modes currently captured are:
              <ul>
                <li>
                  an essential service required for observing is not available, e.g. the camera or focus
                </li>
                <li>a power fault has been detected, e.g. running on batteries due to power outage</li>
                <li>
                  the telescope is currently performing a house-keeping task, e.g. opening and focusing after a period
                  of bad weather
                </li>
                <li>
                  the hardware system suffered a failure
                </li>
                <li>
                  the software system suffered a failure
                </li>
              </ul>
            </li>
          </ul>
          <p class="h4">Camera status</p>
          <p>
            <strong>Failed</strong>
            The camera or filter wheel suffered a hardware failure. They may recover automatically in a few minutes.
          </p>
          <p class="h4">Progress</p>
          <p>
            <strong>Error on last observation</strong>
            The most recent observation ended in failure either due to a technical fault or because it was cancelled.
            See the <span style="font-style: italic">Observatory status</span> status for additional information on
            faults.
          </p>
        </div>
      </status-info-modal>
    </div>
    <div class='container'>
      <div class='col-md-12'>
        <br>
        <br>
        <SessionTable :telescope="telescope"
                      :session="session"
                      :status="agentStatus"
                      :propId="propId"
                      :archiveLink="archiveLink">
        </SessionTable>
      </div>
    </div>
    <div class='container'>
      <div class='footer col-md-12'>
        Copyright © 2018 Las Cumbres Observatory. All rights reserved.
        <br>
      </div>
    </div>
  </div>
</template>

<script>
  import Moment from 'moment';
  import { bridgeConnection } from '../js/common';
  import SessionTable from 'sessionTable.vue';
  import SessionStatus from 'sessionStatus.vue';
  import TimePanel from 'timePanel.vue';
  import ObservationPanel from 'observationPanel.vue';
  import PageHeading from 'pageHeading.vue';
  import StatusInfoModal from 'statusInfoModal.vue';
  import HelpModal from 'helpModal.vue';
  import PageAlert from 'pageAlert.vue';
  import '../img/loading.jpg';
  import '../img/noImageAvailableYet.jpg';

  export default {
    name: 'rti',
    data: function() {
      return {
        pollIntervalSeconds: 1,
        utcNow: this.getCurrentUTC(),
        rtiBridgeUrl: bridgeConnection.bridgeUrl,
        isSessionActive: true,
        agentStatus: {
          'availability': '',
          'telescope': '',
          'instrument': '',
          'progress': ''
        },
        submittedExposureTimes: [],
        timer: null,
        isBridgeConnected: true,
        isAgentConnected: true,
        propId: 'FTPEPO2017AB-002'
      };
    },
    components: {
      HelpModal,
      StatusInfoModal,
      PageHeading,
      TimePanel,
      SessionTable,
      SessionStatus,
      ObservationPanel,
      PageAlert
    },
    props: ['session'],
    beforeRouteEnter(to, from, next) {
      next(vm => {
        if (!vm.session)
          next('/');
      });
    },
    mounted: function() {
      this.timer = setInterval(
        this.tick, 1000 * this.pollIntervalSeconds
      );
    },
    beforeDestroy: function() {
      clearInterval(this.timer);
    },
    computed: {
      telescope: function() {
        return JSON.parse(this.session.telescopeInfo);
      },
      sessionActive: function() {
        return !(this.utcNow < this.session.activeSessionStart || this.utcNow >= this.session.activeSessionEnd);
      },
      archiveLink: function() {
        let startTime = Moment(this.session.activeSessionStart, 'X').utc().format('YYYY-MM-DD%20HH:mm');
        let endTime = Moment(this.session.activeSessionEnd, 'X').utc().format('YYYY-MM-DD%20HH:mm');

        return 'https://archive.lco.global/?PROPID=' + this.propId + '&SITEID=' + this.telescope.site + '&TELID=' +
          this.telescope.telescope + '&start=' + startTime + '&end=' + endTime;
      }
    },
    methods: {
      tick: function() {
        this.getAgentStatus();
        this.updateCurrentUTC();

        bridgeConnection.isConnected((results) => {
          this.isBridgeConnected = results;
        });
      },
      getCurrentUTC: function() {
        return Moment.utc().unix();
      },
      updateCurrentUTC: function() {
        this.utcNow = this.getCurrentUTC();
      },
      getAgentStatus: function() {
        let xhr = new XMLHttpRequest();
        xhr.open('GET', this.rtiBridgeUrl + '/status', true);
        xhr.setRequestHeader('Content-type', 'application/json');
        xhr.setRequestHeader('Authorization', this.session.token);
        xhr.send();

        xhr.onload = () => {
          if (xhr.status === 403)
            this.$router.push('/');

          let response = JSON.parse(xhr.responseText);

          if (response.status) {
            this.agentStatus = response.status;
            this.isAgentConnected = true;
          } else {
            this.isAgentConnected = false;
          }
        };

        xhr.onerror = () => {
          this.agentStatus = {
            availability: '',
            telescope: '',
            instrument: '',
            progress: ''
          };
        };
      },
      setSubmittedExposureTimes: function(lastSubmittedExposureTimes) {
        this.submittedExposureTimes = lastSubmittedExposureTimes;
      }
    }
  };
</script>
